import React, { useState, useRef, useEffect } from "react";
import { ArrowLeft, Printer, ChefHat, Save, CheckCircle, Edit, Copy, Trash2, X, Camera, FileImage, MessageSquare, GripVertical, Plus, FileText, Monitor, Music, Zap, Globe, Clock, Disc } from "lucide-react";
import { supabase } from "../supabaseClient"; 
import CategoryAccordion from "./common/CategoryAccordion";
import { calculateTotals, capitalize } from "../utils/helpers";
import { EVENT_TYPES, STATUSES } from "../data/constants";
import { createStyles } from "../styles"; 

const BanquetEditor = ({
  currentBanquet, setCurrentBanquet, dishes, setDishes, categories, setCategories,
  setView, banquets, setBanquets, isMobile, t, isDark, companyLogo, halls, currentUser
}) => {
  const styles = createStyles(isMobile, isDark);

  const [searchTerm, setSearchTerm] = useState("");
  const [pickerMenuType, setPickerMenuType] = useState("banquet");
  const [printMode, setPrintMode] = useState(""); 
  const [targetGroupId, setTargetGroupId] = useState(null); 
  const [isLargeFont, setIsLargeFont] = useState(false); // Состояние для крупного шрифта
  
  const [editingDish, setEditingDish] = useState(null);
  const [saveStatus, setSaveStatus] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const dragGroup = useRef(null);
  const dragOverGroup = useRef(null);

  // ------------------------------------------------------------------
  // ЛОГИКА ПОДСЧЕТОВ (С РУЧНЫМ ВВОДОМ МЕНЮ И ИТОГА)
  // ------------------------------------------------------------------
  
  const { subtotal: autoSubtotal } = calculateTotals(currentBanquet);
  const activeSubtotal = currentBanquet.customSubtotalPrice ? Number(currentBanquet.customSubtotalPrice) : autoSubtotal;
  const activeServiceAmount = activeSubtotal * ((currentBanquet.servicePercent || 0) / 100);
  const autoTotalRevenue = activeSubtotal + activeServiceAmount;
  const activeTotalRevenue = currentBanquet.customTotalPrice ? Number(currentBanquet.customTotalPrice) : autoTotalRevenue;
  const toPayAmount = activeTotalRevenue - (currentBanquet.prepayment || 0);
  const displayedPricePerPerson = currentBanquet.pricePerPerson ? currentBanquet.pricePerPerson : (currentBanquet.guests > 0 ? Math.round(activeTotalRevenue / currentBanquet.guests) : 0);
  
  // ------------------------------------------------------------------

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!currentBanquet.techEquipment) {
        setCurrentBanquet(prev => ({ ...prev, techEquipment: { ledCount: 0, micCount: 0, light: false, sound: false, dj: false, photoVideo: false, techAmount: "" } }));
    }
  }, []);

  const handlePhotoDisabled = () => { alert(t.photoNotAvailable); };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return dateString.split("-").reverse().join(".");
  };

  const filteredDishes = dishes.filter(
    (d) => d.name.toLowerCase().includes(searchTerm.toLowerCase()) && (d.type || "banquet") === pickerMenuType
  );
    
  const updateBanquet = (f, v) => {
      setCurrentBanquet({ ...currentBanquet, [f]: v });
      setHasUnsavedChanges(true); 
  };

  const updateTech = (field, value) => {
      const prevTech = currentBanquet.techEquipment || { ledCount: 0, micCount: 0, light: false, sound: false, dj: false, photoVideo: false, techAmount: "" };
      updateBanquet("techEquipment", { ...prevTech, [field]: value });
  };

  const addDishToBanquet = (dish) => {
      const gid = targetGroupId || dish.category_name; 
      const newItem = { 
        ...dish, 
        quantity: currentBanquet.guests || 1, 
        uid: Date.now() + Math.random(),
        groupId: gid 
      };
      updateBanquet("items", [...(currentBanquet.items || []), newItem]);
  };

  const saveBanquet = async (dataOverride = null) => {
    const banquetDataToSave = (dataOverride && !dataOverride.preventDefault) ? dataOverride : currentBanquet;

    if (!banquetDataToSave.name) return alert("Introduceți numele clientului"); 
    if (!banquetDataToSave.date) return alert("Alegeți data");

    setIsSaving(true);
    setSaveStatus("Salvare...");

    try {
        const finalTotal = banquetDataToSave.customTotalPrice 
            ? Number(banquetDataToSave.customTotalPrice) 
            : calculateTotals(banquetDataToSave).totalRevenue;

        const payload = {
            client_name: banquetDataToSave.name,
            event_date: banquetDataToSave.date,
            start_time: banquetDataToSave.startTime,
            event_type: banquetDataToSave.eventType,
            hall_name: banquetDataToSave.hall,
            guests_count: banquetDataToSave.guests || 0,
            prepayment: banquetDataToSave.prepayment || 0,
            service_percent: banquetDataToSave.servicePercent || 15,
            status: banquetDataToSave.status || 'draft',
            total_price: finalTotal,
            tech_equipment: banquetDataToSave.techEquipment,
            category_notes: banquetDataToSave.categoryNotes || {},
            price_per_person: Number(banquetDataToSave.pricePerPerson) || 0,
            created_by: banquetDataToSave.created_by || currentUser,
            group_order: banquetDataToSave.groupOrder || null,
            
            custom_subtotal_price: banquetDataToSave.customSubtotalPrice || null,
            custom_total_price: banquetDataToSave.customTotalPrice || null,
            hide_total_price: banquetDataToSave.hideTotalPrice || false
        };

        let banquetId = banquetDataToSave.id;
        let responseData; 

        if (typeof banquetId === 'number' || !banquetId) {
            const { data, error } = await supabase.from('banquets').insert([payload]).select();
            if (error) throw error;
            responseData = data[0]; 
            banquetId = responseData.id;
        } else {
            const { data, error } = await supabase.from('banquets').update(payload).eq('id', banquetId).select();
            if (error) throw error;
            responseData = data[0]; 
        }

        if (banquetId) {
            await supabase.from('banquet_items').delete().eq('banquet_id', banquetId);
            const itemsToInsert = (banquetDataToSave.items || []).map(item => ({
                banquet_id: banquetId,
                dish_id: item.id,
                quantity: item.quantity,
                comment: item.comment || "",
                group_id: item.groupId || item.category_name,
                item_price: item.price,
                item_name: item.name
            }));
            if (itemsToInsert.length > 0) {
                const { error: itemsError } = await supabase.from('banquet_items').insert(itemsToInsert);
                if (itemsError) throw itemsError;
            }
        }

        const updatedBanquetsList = banquets.filter(b => b.id !== banquetDataToSave.id);
        
        const newBanquetState = { 
            ...responseData, 
            id: responseData.id, 
            name: responseData.client_name, 
            date: responseData.event_date,
            hall: responseData.hall_name,
            startTime: responseData.start_time,
            eventType: responseData.event_type,
            techEquipment: responseData.tech_equipment,
            guests: responseData.guests_count,
            servicePercent: responseData.service_percent,
            prepayment: responseData.prepayment,
            pricePerPerson: responseData.price_per_person,
            status: responseData.status,
            items: banquetDataToSave.items,
            categoryNotes: responseData.category_notes || banquetDataToSave.categoryNotes || {},
            groupOrder: responseData.group_order || [], 
            
            customSubtotalPrice: responseData.custom_subtotal_price,
            customTotalPrice: responseData.custom_total_price,
            hideTotalPrice: responseData.hide_total_price,
            
            created_by: responseData.created_by 
        };
        
        setBanquets([...updatedBanquetsList, newBanquetState]);
        setCurrentBanquet(newBanquetState);
        
        setHasUnsavedChanges(false);
        setSaveStatus(t.saved);
        setTimeout(() => setSaveStatus(""), 2000);

    } catch (error) {
        console.error("Eroare de salvare:", error);
        alert("Eroare la salvare: " + error.message);
        setSaveStatus("Eroare!");
    } finally {
        setIsSaving(false);
    }
  };

  const saveEditedDishState = async () => {
     const updatedItems = currentBanquet.items.map((item) => 
         item.uid === editingDish.uid ? { ...item, ...editingDish } : item
     );
     const newBanquetState = { ...currentBanquet, items: updatedItems };
     setCurrentBanquet(newBanquetState);
     setEditingDish(null);
     await saveBanquet(newBanquetState);
  };

  const saveAsNewDish = async () => {
    const cat = editingDish.category || editingDish.category_name;
    if (!editingDish.name || !editingDish.price || !cat) return alert("Completați numele, prețul și categoria!");

    try {
        const dishToSave = {
            name: capitalize(editingDish.name),
            price: Number(editingDish.price),
            weight: editingDish.weight,
            category_name: cat,
            type: pickerMenuType,
            image_url: editingDish.image_url || null 
        };
        const { data, error } = await supabase.from('dishes').insert([dishToSave]).select();
        if (error) throw error;
        const savedDish = data[0];
        setDishes([...dishes, savedDish]);
        if (editingDish.uid) {
            const updatedBanquetItems = currentBanquet.items.map((item) => 
                item.uid === editingDish.uid ? { ...savedDish, uid: item.uid, quantity: item.quantity, groupId: item.groupId } : item
            );
            updateBanquet("items", updatedBanquetItems);
        }
        const catExists = categories.some(c => c.name === cat);
        if (!catExists) {
            const { data: newCat } = await supabase.from('categories').insert([{ name: cat }]).select();
            if(newCat) setCategories([...categories, newCat[0]]);
        }
        setEditingDish(null);
        alert("Felul de mâncare a fost creat!");
    } catch (e) { alert("Eroare la creare: " + e.message); }
  };

  const updateGlobalDish = async () => {
      if (!editingDish.id) return; 
      if (!window.confirm("Aceasta va actualiza prețul în TOATE meniurile. Continuați?")) return;
      try {
          const { error } = await supabase.from('dishes').update({
              name: capitalize(editingDish.name),
              price: Number(editingDish.price),
              weight: editingDish.weight,
          }).eq('id', editingDish.id);
          if (error) throw error;
          const updatedDishesList = dishes.map(d => d.id === editingDish.id ? { ...d, ...editingDish } : d);
          setDishes(updatedDishesList);
          await saveEditedDishState(); 
          alert("Actualizat în Meniu!");
      } catch (e) { alert("Eroare de actualizare: " + e.message); }
  };

  const splitCategory = (e, catName) => {
    e.stopPropagation();
    const newGroupId = `${catName}_split_${Date.now()}`;
    const newNotes = { ...(currentBanquet.categoryNotes || {}), [newGroupId] : "" };
    
    updateBanquet("categoryNotes", newNotes);
    setTargetGroupId(newGroupId);
  };

  const updateCategoryNote = (gid, note) => {
    const newNotes = { ...(currentBanquet.categoryNotes || {}), [gid] : note };
    updateBanquet("categoryNotes", newNotes);
  };

  const updateItemComment = (uid, text) => {
    const updatedItems = currentBanquet.items.map(item => 
      item.uid === uid ? { ...item, comment: text } : item
    );
    updateBanquet("items", updatedItems);
  };

  const handleDragStart = (e, position) => { dragItem.current = position; };
  const handleDragEnter = (e, position) => { dragOverItem.current = position; };
  
  const handleDropOnCategory = (e, newGroupId) => {
      e.preventDefault();
      if (dragItem.current !== null) {
          const itemsCopy = [...currentBanquet.items];
          if (itemsCopy[dragItem.current]) {
              itemsCopy[dragItem.current].groupId = newGroupId; 
              updateBanquet("items", itemsCopy);
          }
      }
      dragItem.current = null;
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null) {
        const itemsCopy = [...(currentBanquet.items || [])];
        const dragItemContent = itemsCopy[dragItem.current];
        
        if (itemsCopy[dragOverItem.current]) {
            dragItemContent.groupId = itemsCopy[dragOverItem.current].groupId;
            itemsCopy.splice(dragItem.current, 1);
            itemsCopy.splice(dragOverItem.current, 0, dragItemContent);
            updateBanquet("items", itemsCopy);
        }
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleGroupDragStart = (e, position) => {
      dragGroup.current = position;
  };

  const handleGroupDragEnter = (e, position) => {
      dragOverGroup.current = position;
  };

  const handleGroupDragEnd = () => {
      if (dragGroup.current !== null && dragOverGroup.current !== null) {
          const currentOrderIds = allGroups.map(g => g.id);
          const newOrder = [...currentOrderIds];
          const itemToMove = newOrder[dragGroup.current];
          newOrder.splice(dragGroup.current, 1);
          newOrder.splice(dragOverGroup.current, 0, itemToMove);
          
          updateBanquet("groupOrder", newOrder);
      }
      dragGroup.current = null;
      dragOverGroup.current = null;
  };

  const handleBack = () => { 
    if (hasUnsavedChanges) {
        if (window.confirm("Aveți modificări nesalvate. Sunteți sigur că doriți să ieșiți?")) {
            setView("list");
        }
    } else { setView("list"); }
  };

  const handlePrint = (mode) => {
    setPrintMode(mode);
    setTimeout(() => { window.print(); }, 50);
  };
    
  const handleCreateNewDish = () => {
      setEditingDish({
          uid: Date.now(),
          name: "", price: "", weight: "", category: categories[0]?.name || "",
          type: pickerMenuType, image: null, quantity: 1
      });
  };

  const tech = currentBanquet.techEquipment || { ledCount: 0, micCount: 0, light: false, sound: false, dj: false, photoVideo: false, techAmount: "" };

  let allGroups = [];
  categories.forEach(cat => {
      allGroups.push({ id: cat.name, title: cat.name, isMain: true });
      const splits = Object.keys(currentBanquet.categoryNotes || {}).filter(k => k.startsWith(`${cat.name}_split_`));
      splits.forEach(s => allGroups.push({ id: s, title: `${cat.name}`, isMain: false }));
  });

  if (currentBanquet.groupOrder && currentBanquet.groupOrder.length > 0) {
      allGroups.sort((a, b) => {
          const indexA = currentBanquet.groupOrder.indexOf(a.id);
          const indexB = currentBanquet.groupOrder.indexOf(b.id);
          const valA = indexA === -1 ? 9999 : indexA;
          const valB = indexB === -1 ? 9999 : indexB;
          return valA - valB;
      });
  }

  return (
    <div style={styles.container}>
      <style>{`
      @media print {
          /* ОБЩИЕ НАСТРОЙКИ */
          .no-print { display: none !important; }
          .only-print { display: block !important; }
          body { background: white; color: black; margin: 0 !important; padding: 0 !important; }
          .rounded-table-container { min-width: initial !important; width: 100% !important; border: none !important; }
          
          /* ТАБЛИЦА */
          table { 
              width: 100% !important; 
              min-width: 0 !important; 
              table-layout: fixed !important; 
              border-collapse: collapse !important;
          }
          
          /* ЯЧЕЙКИ */
          table td, table th { 
              padding: 1px 2px !important; 
              line-height: 1.1 !important; 
              vertical-align: top !important; 
              font-size: 11px !important; 
          }
          
          /* Убираем flex-сетку для печати, чтобы длинные названия не создавали пустых дыр */
          .name-wrapper { 
              display: block !important; 
              margin: 0 !important;
              padding: 0 !important;
          }
          
          /* Скрываем поле примечания, если оно пустое */
          .name-wrapper input:placeholder-shown {
              display: none !important;
          }
          
          /* Настройки для ЗАПОЛНЕННОГО примечания (чтобы было на одной строке) */
          .name-wrapper input {
              display: inline-block !important;
              width: auto !important;
              font-size: 11px !important;
              font-style: italic !important;
              color: #555 !important;
              margin: 0 0 0 5px !important;
              padding: 0 !important;
              border: none !important;
              background: transparent !important;
          }
          
          .cat-note-input:placeholder-shown {
              display: none !important;
          }

          /* --- НАСТРОЙКИ КОЛОНОК --- */
          .col-name { 
              width: 52% !important; 
              /* Правила для разрыва длинных слов: */
              white-space: normal !important;
              overflow-wrap: break-word !important;
              word-break: break-word !important;
          } 

          /* ВЫХОД - добавляем отступ справа, чтобы не сливалось с кол-вом */
          .col-weight { 
              width: 18% !important; 
              white-space: normal !important;   
              word-wrap: break-word !important; 
              word-break: break-all !important; 
              text-align: right !important; 
              padding-right: 20px !important;
          }

          /* КОЛ-ВО - по центру */
          .col-qty { 
              width: 5% !important; 
              white-space: nowrap !important; 
              text-align: center !important; 
          }

          .price-col-table { width: 11% !important; white-space: nowrap !important; text-align: right !important; }
          .col-sum { width: 14% !important; white-space: nowrap !important; text-align: right !important; }

          /* --- ЛОГИКА КРУПНОГО ШРИФТА --- */
          ${isLargeFont ? `
              table td, table th { font-size: 14px !important; }
              .col-name { width: 42% !important; }
              .col-weight { width: 18% !important; font-size: 12px !important; }
              .col-qty { width: 10% !important; }
              .col-sum, .price-col-table { width: 15% !important; }
              h2 { font-size: 24px !important; margin-bottom: 5px !important; }
              p { font-size: 16px !important; }
          ` : ''}

          /* ПРИНУДИТЕЛЬНО ПЕЧАТАТЬ ВЕРТИКАЛЬНО */
          @page {
              size: portrait;
              margin: 10mm;
          }

          /* --- РЕЖИМ: КУХНЯ --- */
          ${(printMode === 'kitchen') ? `
              .price-col, .col-sum, .financial-block, .summary-section, .client-visible { display: none !important; }
              .print-content-wrapper { 
                  width: 70% !important; 
                  max-width: 70% !important; 
                  float: left !important; 
                  border-right: 1px dashed #000 !important; 
                  padding-right: 10px !important; 
                  margin-right: 0 !important;
              }
              .col-name { width: 65% !important; }
              
              .col-weight { 
                  width: 25% !important; 
                  text-align: right !important; 
                  white-space: normal !important; 
                  word-break: break-all !important; 
                  padding-right: 20px !important;
              }
              .col-qty { width: 10% !important; text-align: center !important; }
          ` : ''}

          /* РЕЖИМ: ТОЛЬКО НАЗВАНИЯ */
          ${(printMode === 'client_clean') ? `
              .col-weight, .col-qty, .price-col-table, .col-sum { display: none !important; }
              .col-name { width: 100% !important; }
              .summary-hide-client { display: none !important; }
              .client-visible { display: flex !important; }
          ` : ''}
      }
      `}</style>

      {/* --- МОДАЛЬНОЕ ОКНО --- */}
      {editingDish && (
        <div style={styles.editor.modalOverlay}>
          <div style={styles.editor.modalContent}>
            <button onClick={() => setEditingDish(null)} style={{ position: "absolute", top: "10px", right: "10px", background: "none", border: "none", cursor: "pointer" }}><X size={24} color={styles.colors.text} /></button>
            <h3>{editingDish.id ? t.editDish : t.addDish}</h3>
              
            <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "15px", justifyContent: "center" }}>
               <div style={{ width: "80px", height: "80px", borderRadius: "8px", background: styles.colors.inputBg, border: `1px dashed ${styles.colors.inputBorder}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {(editingDish.image || editingDish.image_url) ? <img src={editingDish.image || editingDish.image_url} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" /> : <FileImage size={24} color={styles.colors.muted} />}
              </div>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={handlePhotoDisabled}><Camera size={16} /> {t.uploadPhoto}</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              <div>
                <label style={{ fontSize: "12px", color: styles.colors.muted, marginBottom: "5px", display: "block" }}>{t.category}</label>
                <input list="category-options" style={styles.input} value={editingDish.category || editingDish.category_name || ""} onChange={(e) => setEditingDish({ ...editingDish, category: capitalize(e.target.value), category_name: capitalize(e.target.value) })} placeholder={t.catPlaceholder} />
                <datalist id="category-options">{categories.map((c) => <option key={c.id} value={c.name} />)}</datalist>
              </div>
              <div>
                <label style={{ fontSize: "12px", color: styles.colors.muted, marginBottom: "5px", display: "block" }}>{t.dishName}</label>
                <input style={styles.input} value={editingDish.name} onChange={(e) => setEditingDish({ ...editingDish, name: capitalize(e.target.value) })} />
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                  <div style={{ flex: 1 }}><label style={{ fontSize: "12px", color: styles.colors.muted, marginBottom: "5px", display: "block" }}>{t.weight}</label><input style={styles.input} value={editingDish.weight} onChange={(e) => setEditingDish({ ...editingDish, weight: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={{ fontSize: "12px", color: styles.colors.muted, marginBottom: "5px", display: "block" }}>{t.price}</label><input type="number" style={styles.input} value={editingDish.price} onChange={(e) => setEditingDish({ ...editingDish, price: e.target.value })} /></div>
              </div>
              
              <div style={{ display: "flex", gap: "10px", marginTop: "10px", flexWrap: "wrap", flexDirection: "column" }}>
                {editingDish.id ? (
                    <>
                          <button style={{ ...styles.btn, backgroundColor: "#16a34a", color: "white", width: '100%' }} onClick={saveEditedDishState}>{t.saveLocal}</button>
                          <div style={{display:'flex', gap: '10px'}}>
                             <button style={{ ...styles.btn, backgroundColor: '#8b5cf6', color: 'white', flex: 1 }} onClick={saveAsNewDish}><Copy size={16}/> {t.saveAsNew}</button>
                             <button style={{ ...styles.btn, backgroundColor: '#ef4444', color: 'white', flex: 1 }} onClick={updateGlobalDish}><Globe size={16}/> {t.updateInMenu}</button>
                          </div>
                    </>
                ) : (
                    <button style={{ ...styles.btn, ...styles.btnPrimary, flex: 1 }} onClick={saveAsNewDish}>{t.saveAsNew}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ШАПКА РЕДАКТОРА */}
      <div style={styles.header} className="no-print">
        <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={handleBack}><ArrowLeft size={16} /> {isMobile ? "" : t.back}</button>
        <h3 style={{ margin: 0, fontSize: isMobile ? "16px" : "20px" }}>{currentBanquet.name || t.newBanquet}</h3>
        
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", alignItems: "center" }}>
          
          {/* ПЕРЕКЛЮЧАТЕЛЬ ШРИФТА С ПЕРЕВОДОМ */}
          <div className="no-print" style={{ display: "flex", alignItems: "center", gap: "5px", marginRight: "10px", padding: "5px 10px", background: isDark ? "#1e293b" : "#f1f5f9", borderRadius: "8px", border: `1px solid ${styles.colors.cardBorder}` }}>
            <input 
              type="checkbox" 
              id="largeFontToggle" 
              checked={isLargeFont} 
              onChange={(e) => setIsLargeFont(e.target.checked)} 
              style={{ cursor: "pointer", width: "18px", height: "18px", margin: 0 }}
            />
            <label htmlFor="largeFontToggle" style={{ fontSize: "12px", cursor: "pointer", fontWeight: "bold", color: styles.colors.text, margin: 0 }}>
              {isMobile ? "АА" : t.largeFont || "Крупный шрифт"} {/* Заменили текст на t.largeFont */}
            </label>
          </div>

          <button style={{ ...styles.btn, ...styles.btnSecondary, padding: "10px 15px", borderColor: "#10b981", color: "#10b981" }} onClick={() => handlePrint('client_clean')} title="Только названия"><Printer size={16} /> {t.printClient}</button>
          <button style={{ ...styles.btn, ...styles.btnSecondary, padding: "10px 15px", borderColor: "#8b5cf6", color: "#8b5cf6" }} onClick={() => handlePrint('client_invoice')} title="Счет"><FileText size={16} /> {t.printInvoice}</button>
          <button style={{ ...styles.btn, ...styles.btnSecondary, padding: "10px 15px", color: "#d97706", borderColor: "#d97706" }} onClick={() => handlePrint('kitchen')} title="Кухня"><ChefHat size={16} /> {t.printKitchen}</button>
          <button style={{ ...styles.btn, ...styles.btnPrimary, backgroundColor: saveStatus ? "#16a34a" : "#3b82f6" }} onClick={() => saveBanquet(null)} disabled={isSaving}>
            {saveStatus ? <CheckCircle size={16} /> : <Save size={16} />} {saveStatus || (isSaving ? "Загрузка..." : (isMobile ? "" : t.save))}
          </button>
        </div>
      </div>

      <div style={styles.card} className="no-print">
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "15px", marginBottom: "15px" }}>
            <div>
            <span style={styles.label}>{t.eventType}</span>
            {!EVENT_TYPES.includes(currentBanquet.eventType) ? (
              <div style={{ display: "flex", gap: "5px" }}>
                <input style={{ ...styles.input, borderColor: "#3b82f6" }} autoFocus placeholder={t.addCustom} value={currentBanquet.eventType} onChange={(e) => updateBanquet("eventType", e.target.value)} />
                <button onClick={() => updateBanquet("eventType", EVENT_TYPES[0])} style={{ ...styles.btn, ...styles.btnSecondary, padding: "0 10px", borderColor: "#ef4444", color: "#ef4444" }}><X size={16} /></button>
              </div>
            ) : (
              <select style={styles.select} value={currentBanquet.eventType} onChange={(e) => { if (e.target.value === "CUSTOM_NEW_EVENT") { updateBanquet("eventType", ""); } else { updateBanquet("eventType", e.target.value); } }}>
                {EVENT_TYPES.map((type) => (<option key={type} value={type}>{type}</option>))}
                <option value="CUSTOM_NEW_EVENT" style={{ fontWeight: "bold", color: "#3b82f6" }}>{t.addCustom}</option>
              </select>
            )}
          </div>
          <div><span style={styles.label}>{t.clientName}</span><input style={styles.input} value={currentBanquet.name} onChange={(e) => updateBanquet("name", e.target.value)} /></div>
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}><span style={styles.label}>{t.date}</span><input type="date" style={styles.input} value={currentBanquet.date} onChange={(e) => updateBanquet("date", e.target.value)} /></div>
            <div style={{ flex: 1 }}><span style={styles.label}>{t.startTime}</span><input type="text" placeholder="14:00" style={styles.input} value={currentBanquet.startTime || ""} onChange={(e) => updateBanquet("startTime", e.target.value)} /></div>
          </div>
          <div>
            <span style={styles.label}>Статус</span>
            <select style={{ ...styles.select, fontWeight: "bold", color: STATUSES.find((s) => s.label === currentBanquet.status)?.color }} value={currentBanquet.status} onChange={(e) => updateBanquet("status", e.target.value)}>
              {STATUSES.map((s) => (<option key={s.label} value={s.label}>{t[s.label] || s.label}</option>))}
            </select>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "15px" }}>
          <div>
            <span style={styles.label}>{t.hall}</span>
            <select style={styles.select} value={currentBanquet.hall} onChange={(e) => updateBanquet("hall", e.target.value)}>
               {halls && halls.length > 0 ? (halls.map((h) => (<option key={h.id} value={h.name}>{h.name}</option>))) : (<option value="">Нет залов</option>)}
            </select>
          </div>
          <div><span style={styles.label}>{t.guests}</span><input type="number" style={styles.input} value={currentBanquet.guests || ""} placeholder="0" onChange={(e) => updateBanquet("guests", Number(e.target.value))} /></div>
          <div><span style={styles.label}>{t.prepayment}</span><input type="number" style={styles.input} value={currentBanquet.prepayment || ""} placeholder="0" onChange={(e) => updateBanquet("prepayment", Number(e.target.value))} /></div>
          <div><span style={styles.label}>{t.servicePercent}</span><input type="number" style={styles.input} value={currentBanquet.servicePercent || ""} placeholder="0" onChange={(e) => updateBanquet("servicePercent", Number(e.target.value))} /></div>
        </div>
      </div>

      <div style={styles.editor.grid}>
        <div style={styles.editor.sidebar} className="no-print">
          
          <div style={{ marginBottom: "10px" }}>
             <span style={{ fontSize: '11px', color: styles.colors.muted }}>Добавлять в:</span>
             <select style={{ ...styles.select, fontSize: '12px', padding: '4px' }} value={targetGroupId || ""} onChange={(e) => setTargetGroupId(e.target.value || null)}>
               <option value="">По умолчанию</option>
               {allGroups.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
             </select>
          </div>

          <div style={{ display: "flex", gap: "5px", marginBottom: "10px" }}>
            <button onClick={() => setPickerMenuType("banquet")} style={{ ...styles.btn, flex: 1, padding: "6px", fontSize: "12px", ...(pickerMenuType === "banquet" ? styles.btnPrimary : styles.btnSecondary) }}>{t.typeBanquet}</button>
            <button onClick={() => setPickerMenuType("buffet")} style={{ ...styles.btn, flex: 1, padding: "6px", fontSize: "12px", ...(pickerMenuType === "buffet" ? styles.btnPrimary : styles.btnSecondary) }}>{t.typeBuffet}</button>
          </div>
            
          <input style={{ ...styles.input, marginBottom: "10px" }} placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            
          <button style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", marginBottom: "15px", display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }} onClick={handleCreateNewDish}>
            <Plus size={16} /> {t.addDish}
          </button>

          {categories.map((c) => {
            const items = filteredDishes.filter((d) => d.category_name === c.name).sort((a, b) => a.name.localeCompare(b.name));
            if (items.length === 0) return null;
            return (
              <CategoryAccordion key={c.id} title={c.name} count={items.length} styles={styles} forceOpen={searchTerm.length > 0}
                action={<button onClick={(e) => splitCategory(e, c.name)} style={{ border:'none', background:'none', color:'#3b82f6', cursor:'pointer' }} title="Дублировать группу"><Copy size={16} /></button>}
              >
                <div style={{ padding: "5px" }}>
                  {items.map((d) => (
                    <div key={d.id} style={{ borderBottom: `1px solid ${styles.colors.inputBorder}`, marginBottom: "5px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ flex: 1, padding: "8px", cursor: "pointer", display: "flex", gap: "10px", alignItems: "center" }} onClick={() => addDishToBanquet(d)}>
                        {d.image_url && <img src={d.image_url} style={{ width: "30px", height: "30px", borderRadius: "4px", objectFit: "cover" }} alt="" />}
                        <div style={{ flex: 1, fontSize: "14px" }}>{d.name} <span style={{ color: styles.colors.muted, fontSize: "12px" }}>({d.weight})</span></div>
                        <div style={{ fontSize: "13px", fontWeight: "bold", color: "#2563eb" }}>{d.price}</div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setEditingDish({ ...d, uid: Date.now() }); }} style={{ border: "none", background: styles.colors.inputBg, padding: "8px", cursor: "pointer", borderLeft: `1px solid ${styles.colors.inputBorder}`, color: "#64748b" }} title={t.editDish}><Edit size={16} /></button>
                    </div>
                  ))}
                </div>
              </CategoryAccordion>
            );
          })}
        </div>

        <div id="print-area" className={printMode}>
          <div className="print-content-wrapper">
            
             {/* ШАПКА ДЛЯ ПЕЧАТИ - Обновленная */}
             <div className="only-print" style={{ display: "none", marginBottom: "0px", textAlign: "left" }}>
              {companyLogo && <img src={companyLogo} alt="Logo" style={{ maxHeight: "60px", margin: "0 auto 5px auto", display: "block" }} />}
              
              <h2 style={{ marginTop: 0, margin: '0 0 3px 0', fontSize: '20px' }}>
                {currentBanquet.eventType}: {currentBanquet.name}
              </h2>
              
              <p style={{ fontWeight: "bold", fontSize: "14px", margin: "0" }}>
                {t.hall}: {currentBanquet.hall} | {t.date}: {formatDate(currentBanquet.date)} | {t.startTime}: {currentBanquet.startTime || "—"} | {t.guests}: {currentBanquet.guests}
              </p>
            </div>

            <div className="rounded-table-container" style={{ 
                background: styles.colors.cardBg, 
                borderRadius: "12px", 
                border: `1px solid ${styles.colors.cardBorder}`, 
                overflow: "hidden", 
                overflowX: "auto" 
            }}>
              <table style={{ ...styles.table, minWidth: "700px" }}>
                <thead>
                  <tr>
                    <th className="no-print" style={{ ...styles.th, width: "40px", textAlign: "center" }}></th>
                    <th style={{ ...styles.th, width: "35%" }} className="col-name">{t.colDish}</th>
                    <th style={{ ...styles.thRight, width: "25%" }} className="text-right-print col-weight">{t.colWeight}</th>
                    {/* Выровняли заголовок Кол-во по центру */}
                    <th style={{ ...styles.thRight, width: "10%", textAlign: "center" }} className="col-qty">{t.colQty}</th>
                    <th style={{ ...styles.thRight, width: "5%" }} className="text-right-print price-col price-col-table">{t.colPrice}</th>
                    <th style={{ ...styles.thRight, width: "5%" }} className="text-right-print price-col col-sum">{t.colSum}</th>
                    <th className="no-print" style={{ ...styles.th, width: "10%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {allGroups.map((group) => {
                    const itemsInGroup = (currentBanquet.items || []).filter((item) => (item.groupId || item.category_name) === group.id);
                    if (itemsInGroup.length === 0 && group.isMain) return null;

                    return (
                      <React.Fragment key={group.id}>
                        <tr 
                            draggable 
                            onDragStart={(e) => handleGroupDragStart(e, allGroups.indexOf(group))}
                            onDragEnter={(e) => handleGroupDragEnter(e, allGroups.indexOf(group))}
                            onDragEnd={handleGroupDragEnd}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handleDropOnCategory(e, group.id)}
                        >
                          <td colSpan={100} className="cat-header-print" style={{ ...styles.td, background: isDark ? "#1e293b" : "#f1f5f9", fontWeight: "bold", textTransform: "uppercase", fontSize: "12px", padding: "8px 10px", color: "#3b82f6", borderBottom: `2px solid ${styles.colors.cardBorder}`, cursor: 'move' }}>
                            <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "10px" }}>
                              <div className="no-print" style={{ cursor: "grab", color: styles.colors.muted }}>
                                 <GripVertical size={18} />
                              </div>
                              <span style={{ 
                                  wordBreak: "normal", 
                                  overflowWrap: "break-word", 
                                  whiteSpace: "normal", 
                                  minWidth: "150px", 
                                  lineHeight: "1.2",
                                  marginRight: "10px"
                              }}>
                                {group.title}
                              </span>
                              <div style={{ display: "flex", alignItems: "center", gap: "5px", width: "100%" }}>
                                <MessageSquare size={14} color={styles.colors.muted} className="note-icon" />
                                <input type="text" value={currentBanquet.categoryNotes?.[group.id] || ""} placeholder={t.catNotePlaceholder} onChange={(e) => updateCategoryNote(group.id, e.target.value)} className="cat-note-input" style={{ border: `1px solid ${styles.colors.inputBorder}`, padding: "2px 5px", borderRadius: "4px", background: styles.colors.inputBg, color: styles.colors.text, fontSize: "11px", width: "100%" }} />
                                {!group.isMain && itemsInGroup.length === 0 && (
                                   <button onClick={() => { const newNotes = {...currentBanquet.categoryNotes}; delete newNotes[group.id]; updateBanquet("categoryNotes", newNotes); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor:'pointer' }}><Trash2 size={14}/></button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                        {itemsInGroup.length === 0 && (
                          <tr className="no-print">
                            <td 
                              colSpan={100} 
                              onDragOver={(e) => e.preventDefault()} 
                              onDrop={(e) => handleDropOnCategory(e, group.id)}
                              style={{ textAlign: 'center', padding: '15px', color: styles.colors.muted, borderBottom: '1px dashed #ccc', cursor: 'pointer' }}
                            >
                               {t.emptyArchive || "Перетащите сюда блюдо или выберите слева"}
                            </td>
                          </tr>
                        )}
                        {itemsInGroup.map((i, index) => {
                            const realIndex = (currentBanquet.items || []).indexOf(i);
                            return (
                              <tr key={i.uid} className="item-row" draggable onDragStart={(e) => handleDragStart(e, realIndex)} onDragEnter={(e) => handleDragEnter(e, realIndex)} onDragEnd={handleDragEnd} onDragOver={(e) => e.preventDefault()}>
                                <td className="no-print" style={{ ...styles.td, width: "40px", textAlign: "center", cursor: "grab" }}>
                                    <div style={{styles: styles.editor.dragHandle}}><GripVertical size={20} /></div>
                                </td>
                                <td style={styles.td} className="col-name">
                                  <div className="name-wrapper" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", marginBottom: '4px', gap: "20px" }}>
                                    <span style={{ fontWeight: "500" }}>{i.name}</span>
                                    <input type="text" placeholder="Примечание..." value={i.comment || ""} onChange={(e) => updateItemComment(i.uid, e.target.value)} style={styles.editor.itemComment} className="no-print-border" />
                                  </div>
                                </td>
                                <td style={styles.tdRight} className="text-right-print col-weight">{i.weight}</td>
                                {/* Выровняли инпут Кол-во по центру */}
                                <td style={{ ...styles.tdRight, textAlign: "center" }} className="col-qty">
                                  <input type="number" step="any" value={i.quantity || ""} className="qty-input no-print-border" style={{ ...styles.input, padding: '5px', textAlign: 'center' }} onChange={(e) => { const newItems = currentBanquet.items.map((x) => x.uid === i.uid ? { ...x, quantity: Number(e.target.value) } : x); updateBanquet("items", newItems); }} />
                                </td>
                                <td style={styles.tdRight} className="text-right-print price-col price-col-table">{i.price}</td>
                                <td style={styles.tdRight} className="text-right-print price-col col-sum"><b>{i.price * i.quantity}</b></td>
                                <td className="no-print" style={{ ...styles.td, textAlign: "right" }}>
                                  <div style={{ display: "flex", gap: "5px", justifyContent: "flex-end" }}>
                                    <button onClick={() => setEditingDish({...i, id: i.id})} style={{ border: "none", background: "none", color: "#3b82f6", cursor: "pointer" }}><Edit size={16} /></button>
                                    <button onClick={() => updateBanquet("items", currentBanquet.items.filter((x) => x.uid !== i.uid))} style={{ border: "none", background: "none", color: "#ef4444", cursor: "pointer" }}><Trash2 size={16} /></button>
                                  </div>
                                </td>
                              </tr>
                            );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
              
            <div className="summary-section" style={{ marginTop: "0px" }}>
              <div style={styles.editor.summaryBox}>
              <div className="financial-block">
                  
                  {/* 1. ВСЕГО ПО МЕНЮ */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px", height: "28px" }} className="price-col summary-hide-client">
                      <span style={{ fontSize: "14px" }}>{t.totalMenu}:</span>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px", minWidth: "250px" }}>
                          <div className="no-print">
                              <input 
                                  type="number" 
                                  placeholder={autoSubtotal} 
                                  value={currentBanquet.customSubtotalPrice ?? ""} 
                                  onChange={(e) => updateBanquet("customSubtotalPrice", e.target.value === "" ? null : Number(e.target.value))} 
                                  style={{ 
                                      width: "110px", 
                                      textAlign: "right", 
                                      border: "1px solid #3b82f6", 
                                      padding: "4px", 
                                      borderRadius: "4px", 
                                      fontWeight: "bold",
                                      color: "#3b82f6",
                                      fontSize: "14px",
                                      backgroundColor: currentBanquet.customSubtotalPrice ? "#eff6ff" : "white"
                                  }} 
                              />
                          </div>
                          <b style={{ width: "70px", textAlign: "right", fontSize: "14px" }}>{activeSubtotal}</b>
                      </div>
                  </div>

                  {/* 2. ОБСЛУЖИВАНИЕ */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px", height: "28px", color: styles.colors.muted }} className="price-col summary-hide-client">
                      <span className="text-muted-print" style={{ fontSize: "14px" }}>{t.service} ({currentBanquet.servicePercent}%):</span>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px", minWidth: "250px" }}>
                          <div className="no-print" style={{ width: "110px" }}></div>
                          <b style={{ width: "70px", textAlign: "right", fontSize: "14px" }}>{activeServiceAmount.toFixed(0)}</b>
                      </div>
                  </div>

                  {/* 3. АВАНС (ПРЕДОПЛАТА) */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px", height: "28px", color: "#16a34a" }} className="price-col">
                      <span className="text-green-print" style={{ fontSize: "14px" }}>{t.prepayment}:</span>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px", minWidth: "250px" }}>
                          <div className="no-print" style={{ width: "110px" }}></div>
                          <b className="text-green-print" style={{ width: "70px", textAlign: "right", fontSize: "14px" }}>-{currentBanquet.prepayment}</b>
                      </div>
                  </div>

                  {/* 4. ЦЕНА ЗА ПЕРСОНУ */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px", height: "28px", color: "#3b82f6" }} className="client-visible">
                      <span className="text-blue-print" style={{ fontSize: "14px" }}>{t.pricePerPerson}:</span>
                      
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px", minWidth: "250px" }}>
                          <div className="no-print">
                              <input 
                                  type="number" 
                                  value={displayedPricePerPerson} 
                                  onChange={(e) => updateBanquet("pricePerPerson", Number(e.target.value))} 
                                  style={{ 
                                      width: "110px", 
                                      textAlign: "right", 
                                      border: "1px solid #ccc", 
                                      padding: "4px", 
                                      borderRadius: "4px", 
                                      fontSize: "14px",
                                      fontWeight: "bold"
                                  }} 
                              />
                          </div>
                          <b className="text-blue-print" style={{ width: "70px", textAlign: "right", fontSize: "14px" }}>
                              {displayedPricePerPerson}
                          </b>
                      </div>
                  </div>

                  {/* 5. ИТОГ К ОПЛАТЕ (КРАСНАЯ СТРОКА) */}
                  <div className="client-visible" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", fontSize: "18px", fontWeight: "bold", color: "#dc2626" }}>
                      <span className="text-red-print">{t.toPay}:</span>
                      
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px", minWidth: "250px" }}>
                          <div className="no-print" style={{ display: "flex", alignItems: "center", marginRight: "5px" }} title="Скрыть итог при печати">
                              <input 
                                  type="checkbox" 
                                  checked={currentBanquet.hideTotalPrice || false} 
                                  onChange={(e) => updateBanquet("hideTotalPrice", e.target.checked)} 
                                  style={{ transform: "scale(1.1)", cursor: "pointer" }}
                              />
                          </div>
                          <div className="no-print">
                              <input 
                                  type="number" 
                                  placeholder={autoTotalRevenue} 
                                  value={currentBanquet.customTotalPrice ?? ""} 
                                  onChange={(e) => updateBanquet("customTotalPrice", e.target.value === "" ? null : Number(e.target.value))} 
                                  style={{ 
                                      width: "110px", 
                                      textAlign: "right", 
                                      border: "1px solid #dc2626", 
                                      padding: "4px", 
                                      borderRadius: "4px", 
                                      fontWeight: "bold",
                                      color: "#dc2626",
                                      fontSize: "16px",
                                      backgroundColor: currentBanquet.customTotalPrice ? "#fffbeb" : "white"
                                  }} 
                              />
                          </div>
                          <span className={currentBanquet.hideTotalPrice ? "no-print text-red-print" : "text-red-print"} style={{ width: "70px", textAlign: "right" }}>
                              {toPayAmount}
                          </span>
                      </div>
                  </div>
              </div> 
               <div className="no-print" style={{ borderTop: "1px dashed #ccc", paddingTop: "15px", marginTop: "10px" }}>
                    <h4 style={{ margin: "0 0 10px 0", fontSize: "14px", display: "flex", alignItems: "center", gap: "5px" }}><Monitor size={14} /> {t.techTitle}</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "13px" }}>
                        <div>
                            <label style={{display: 'block', color: '#666', marginBottom: '3px'}}>{t.techLed}</label>
                            <input type="number" style={{ ...styles.input, padding: '5px' }} value={tech.ledCount} onChange={(e) => updateTech("ledCount", Number(e.target.value))} min="0" />
                        </div>
                        <div>
                            <label style={{display: 'block', color: '#666', marginBottom: '3px'}}>{t.techMic}</label>
                            <input type="number" style={{ ...styles.input, padding: '5px' }} value={tech.micCount} onChange={(e) => updateTech("micCount", Number(e.target.value))} min="0" />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                             <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <input type="checkbox" checked={tech.light} onChange={(e) => updateTech("light", e.target.checked)} id="chk_light" style={{ transform: "scale(1.2)" }}/>
                                <label htmlFor="chk_light" style={{ cursor: "pointer", fontSize: "13px" }}><Zap size={14} style={{verticalAlign: 'middle'}}/> {t.techLight}</label>
                             </div>
                             <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <input type="checkbox" checked={tech.sound} onChange={(e) => updateTech("sound", e.target.checked)} id="chk_sound" style={{ transform: "scale(1.2)" }}/>
                                <label htmlFor="chk_sound" style={{ cursor: "pointer", fontSize: "13px" }}><Music size={14} style={{verticalAlign: 'middle'}}/> {t.techSound}</label>
                             </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <input type="checkbox" checked={tech.dj} onChange={(e) => updateTech("dj", e.target.checked)} id="chk_dj" style={{ transform: "scale(1.2)" }}/>
                                <label htmlFor="chk_dj" style={{ cursor: "pointer", fontSize: "13px" }}><Disc size={14} style={{verticalAlign: 'middle'}}/> DJ</label>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <input type="checkbox" checked={tech.photoVideo} onChange={(e) => updateTech("photoVideo", e.target.checked)} id="chk_photo" style={{ transform: "scale(1.2)" }}/>
                                <label htmlFor="chk_photo" style={{ cursor: "pointer", fontSize: "13px" }}><Camera size={14} style={{verticalAlign: 'middle'}}/> Foto/Video</label>
                            </div>
                        </div>
                        <div>
                            <label style={{display: 'block', color: '#666', marginBottom: '3px'}}>{t.techSum}</label>
                            <input type="text" style={{ ...styles.input, padding: '5px' }} value={tech.techAmount || ""} onChange={(e) => updateTech("techAmount", e.target.value)} placeholder="10000" />
                        </div>
                    </div>
                </div>

                {printMode !== 'kitchen' && (
                    <div className="only-print" style={{ display: "none", marginTop: "5px", borderTop: "1px solid #000", paddingTop: "2px" }}>
                        {(tech.ledCount > 0 || tech.micCount > 0 || tech.light || tech.sound || tech.dj || tech.photoVideo || tech.techAmount) && (
                            <>
                                <h3 style={{ margin: "0 0 2px 0", textTransform: "uppercase", fontSize: "10px" }}>{t.techTitle}</h3>
                                <ul style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0px 15px", listStyle: "none", padding: 0, margin: 0, fontSize: "10px", lineHeight: "1.1" }}>
                                    {tech.ledCount > 0 && (<li style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #ccc", padding: "1px 0" }}><span>{t.techLed}</span><b>{tech.ledCount} {t.pcs}</b></li>)}
                                    {tech.light && (<li style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #ccc", padding: "1px 0" }}><span>{t.techLight}</span><b>✓</b></li>)}
                                    {tech.sound && (<li style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #ccc", padding: "1px 0" }}><span>{t.techSound}</span><b>✓</b></li>)}
                                    {tech.micCount > 0 && (<li style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #ccc", padding: "1px 0" }}><span>{t.techMic}</span><b>{tech.micCount} {t.pcs}</b></li>)}
                                    {tech.dj && (<li style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #ccc", padding: "1px 0" }}><span>DJ</span><b>✓</b></li>)}
                                    {tech.photoVideo && (<li style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #ccc", padding: "1px 0" }}><span>Foto/Video</span><b>✓</b></li>)}
                                    {tech.techAmount && (<li style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed #ccc", gridColumn: "1 / -1", marginTop: "2px", fontWeight: "bold", padding: "1px 0" }}><span>{t.techSum}:</span><span>{tech.techAmount}</span></li>)}
                                </ul>
                            </>
                        )}
                    </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BanquetEditor;